import { FormEditIcon } from 'assets/icons';
import FormCloseReactIcon from 'assets/react-icons/FormCloseReactIcon';
import ItemsTabReactIcon from 'assets/react-icons/ItemsTabReactIcon';
import LineItemPlusReactIcon from 'assets/react-icons/LineItemPlusReactIcon';
import AddAttachmentModal from 'components/add-attachment-modal/AddAttachmentsModal';
import AttachmentLibrary from 'components/attachment-library/AttachmentLibrary';
import { UploadAttachmentToPresignedModal } from 'components';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { PRESIGNED_UPLOAD_USE_CASE } from 'constants/common';
import { useUploadEstimateImageFileMutation } from 'containers/estimates/api';
import {
  modifyEstimateAttachment,
  removeEstimateAttachments,
  resetEstimateAttachmentState,
  selectCascadingRelatedEntityData,
  selectEstimateAttachments,
  updateEstimateAttachments,
  updateMultipleEstimateAttachments
} from 'containers/estimates/estimatesSlice';
import { generateAttachmentSections } from 'containers/estimates/utils';
import { useLazyGetInvoiceAttachmentsQuery } from 'containers/invoices/api';
import { useLazyGetJobAttachmentListQuery } from 'containers/jobs/api';
import { JobAttachmentType } from 'containers/jobs/jobs.model';
import { useAppDispatch, useAppSelector } from 'hooks';
import { FC, useEffect, useRef, useState } from 'react';
import { UploadAttachmentFile, VoidFn } from 'types/baseTypes';

interface EstimateAttachmentsSectionProps {
  className?: string;
  isEdit?: boolean;
  handleOnFocus: VoidFn;
}

const EstimateAttachmentsSection: FC<EstimateAttachmentsSectionProps> = ({ className, handleOnFocus }) => {
  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState({
    isEdit: false,
    open: false,
    attachment: { id: null, url: null, title: null, imagePreview: null }
  });
  const [isAttachmentLibraryOpen, setIsAttachmentLibraryOpen] = useState(false);

  const currentImageUploadCall = useRef(null);

  const currentTeam = useAppSelector(selectCurrentTeam);
  const estimateAttachments = useAppSelector(selectEstimateAttachments);
  const { jobId, invoiceId } = useAppSelector(selectCascadingRelatedEntityData);
  const dispatch = useAppDispatch();

  const [uploadFileMutation, { isLoading: isUploadingImage }] = useUploadEstimateImageFileMutation();
  const [getJobAttachmentListQuery, { data: jobAttachments }] = useLazyGetJobAttachmentListQuery();
  const [getInvoiceAttachmentListQuery, { data: invoiceAttachments }] = useLazyGetInvoiceAttachmentsQuery();

  useEffect(() => {
    if (jobId) {
      getJobAttachmentListQuery({ jobId, teamId: currentTeam.id });
    }
  }, [jobId, currentTeam.id]);

  useEffect(() => {
    if (invoiceId) {
      getInvoiceAttachmentListQuery({ invoiceId, teamId: currentTeam.id });
    }
  }, [invoiceId, currentTeam.id]);

  const handleAddPhotoClick = () => {
    setIsAttachmentModalOpen({
      isEdit: false,
      open: true,
      attachment: { id: null, url: null, title: null, imagePreview: null }
    });
    handleOnFocus();
  };

  const resetAttachmentModalState = () => {
    setIsAttachmentModalOpen({
      isEdit: false,
      open: false,
      attachment: { id: null, url: null, title: null, imagePreview: null }
    });
  };

  const handleAttachmentModalClose = () => {
    if (isUploadingImage) {
      currentImageUploadCall.current.abort();
    }
    resetAttachmentModalState();
  };

  const handleSingleAttachmentSave = async ({ image, title, imagePreview, hasImageChanged }) => {
    if (isAttachmentModalOpen.isEdit) {
      let url = isAttachmentModalOpen.attachment.url;
      if (hasImageChanged) {
        const { data } = await getImageURL(image);
        url = data.url;
      }
      dispatch(
        modifyEstimateAttachment({
          title,
          url,
          imagePreview,
          id: isAttachmentModalOpen.attachment.id,
          source: 'ESTIMATE',
          urlLink: imagePreview
        })
      );
      resetAttachmentModalState();
    }
  };

  const getImageURL = async imageFile => {
    try {
      const imageUrlFromS3Call = uploadFileMutation({
        file: imageFile,
        teamId: currentTeam.id
      });
      currentImageUploadCall.current = imageUrlFromS3Call;
      const imageUrlFromS3 = await imageUrlFromS3Call.unwrap();
      return imageUrlFromS3;
    } catch (err) {
      // Handle error
    }
  };

  const onEditAttachment = (attachment: JobAttachmentType) => {
    setIsAttachmentModalOpen({ attachment: attachment, open: true, isEdit: true });
  };

  const onRemoveAttachment = (attachment: JobAttachmentType) => {
    dispatch(removeEstimateAttachments(attachment));
  };

  const handleOpenAttachmentLibrary = () => {
    setIsAttachmentLibraryOpen(true);
  };

  const handleSaveFromPresignedModal = (attachments: UploadAttachmentFile[]) => {
    dispatch(updateMultipleEstimateAttachments(attachments));
    resetAttachmentModalState();
  };

  const handleSaveFromLibrary = attachments => {
    dispatch(resetEstimateAttachmentState());
    const attachmentsToSave = attachments.map(attachment => ({
      title: attachment.title,
      url: attachment.url,
      imagePreview: attachment.urlLink,
      id: attachment.id,
      source: attachment.source,
      urlLink: attachment.urlLink
    }));
    attachmentsToSave.forEach(attachment => {
      dispatch(updateEstimateAttachments(attachment));
    });
    setIsAttachmentLibraryOpen(false);
  };

  return (
    <div className={className}>
      <div className="mb-2 text-17px font-semibold text-heading">Attachments</div>
      <div className="flex flex-col gap-5">
        {estimateAttachments?.map(attachment => (
          <div
            onClick={() => onEditAttachment(attachment)}
            key={attachment.url}
            className="relative flex min-h-[100px] w-full 
            flex-row rounded border border-borderGray py-4 px-5 last:mb-5">
            <div className="relative mr-4 h-16 w-16 rounded-md">
              <img
                className="absolute left-1/2 h-full -translate-x-1/2 rounded-md object-cover"
                src={attachment.imagePreview}
                alt={attachment.title}
              />
            </div>

            <div className="mb-[6px] break-words text-17px font-semibold text-heading line-clamp-2">
              {attachment.title}
            </div>

            <FormCloseReactIcon
              id="create-estimate-remove-attachment-close-icon"
              onClick={e => {
                e.stopPropagation();
                onRemoveAttachment(attachment);
              }}
              className="absolute top-3 right-[-25px] shrink-0 cursor-pointer 1439px:right-[-30px]"
            />
            <FormEditIcon
              id="create-estimate-attachment-edit-icon"
              onClick={() => onEditAttachment(attachment)}
              className="absolute bottom-3 right-[-25px] mt-3 shrink-0 cursor-pointer 1439px:right-[-30px]"
            />
          </div>
        ))}
      </div>
      <div className="flex w-full cursor-pointer flex-row justify-between rounded border border-borderGray py-4 px-5">
        <div className="flex gap-2" onClick={handleAddPhotoClick}>
          <LineItemPlusReactIcon id="create-estimate-add-photo-icon" className="shrink-0" />
          <div id="estimate-add-photo" className="text-17px font-semibold text-secondary">
            Add photos
          </div>
        </div>
        {(jobId || invoiceId) && (
          <div className="flex cursor-pointer items-center gap-2" onClick={handleOpenAttachmentLibrary}>
            <ItemsTabReactIcon
              id="add-from-library"
              className="path-fill-current rect-stroke-current mr-2 text-secondary"
            />
            <div className="text-17px font-semibold text-secondary">Add from library</div>
          </div>
        )}
      </div>
      {isAttachmentModalOpen.open && isAttachmentModalOpen.isEdit && (
        <AddAttachmentModal
          isEdit={isAttachmentModalOpen.isEdit}
          isUploadingImage={isUploadingImage}
          onClose={handleAttachmentModalClose}
          onSave={handleSingleAttachmentSave}
          attachmentData={isAttachmentModalOpen.attachment}
        />
      )}

      {isAttachmentModalOpen.open && !isAttachmentModalOpen.isEdit && (
        <UploadAttachmentToPresignedModal
          open={isAttachmentModalOpen.open}
          toggleOpen={resetAttachmentModalState}
          onSave={handleSaveFromPresignedModal}
          useCase={PRESIGNED_UPLOAD_USE_CASE.ESTIMATES}
          idPrefix="estimate-upload-attachment"
        />
      )}

      {isAttachmentLibraryOpen && (
        <AttachmentLibrary
          onClose={() => setIsAttachmentLibraryOpen(false)}
          onSave={handleSaveFromLibrary}
          isOpen={isAttachmentLibraryOpen}
          sections={generateAttachmentSections(null, jobAttachments, invoiceAttachments)}
          selectedAttachments={estimateAttachments}
        />
      )}
    </div>
  );
};

export default EstimateAttachmentsSection;
